import { useForm, useStore } from "@tanstack/react-form";
import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { Collapsible, Popover } from "radix-ui";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Checkbox } from "@/components/checkbox";
import { CoursesSelector } from "@/components/forms/courses-selector";
import { FeedsSelector } from "@/components/forms/feeds-selector";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CreateMemberGroupDialog } from "@/dialogs/member-group-create-dialog";
import { useFileUpload } from "@/hooks/useFileUpload";
import { MemberBadge } from "@/ui/member-badge";
import { cn } from "@/utils/cn";
import type { membrosNovoQuery } from "./__generated__/membrosNovoQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/audiencia/membros/novo/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate({ from: Route.fullPath });
	const { club, session } = useRouteContext({ from: Route.id });

	const [createMemberGroupForCourse, setCreateMemberGroupForCourse] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { upload } = useFileUpload({ club: club.slug, session });

	const data = useLazyLoadQuery<membrosNovoQuery>(
		graphql`
			query membrosNovoQuery($club: String!) {
				club(slug: $club) {
					id
					offers(first: 10000) {
						edges {
							node {
								id
								productName
								accessGroup {
									accessGroup {
										spaces {
											id
										}
										courses {
											course {
												id
											}
											memberGroup {
												id
												name
											}
										}
									}
								}
							}
						}
					}
					groups {
						id
						name
						spaces {
							id
							name
							icon
						}
					}
					courses(first: 10000) {
						edges {
							node {
								id
								name
								category {
									id
									name
								}
								memberGroups {
									id
									name
									isDefault
								}
							}
						}
					}
					tags(first: 10000) {
						edges {
							node {
								id
								text
							}
						}
					}
					badges(first: 10000) {
						edges {
							node {
								id
								...memberBadgeFragment
							}
						}
					}
				}
			}
		`,
		{ club: club.slug },
	);

	const [batchImportMembers] = useMutation(graphql`
		mutation membrosNovoBatchImportMutation(
			$club: String!
			$input: BatchImportMembersInput!
		) {
			batchImportMembers(club: $club, batchImportMembersInput: $input)
		}
	`);

	const [manualBatchImportMembers] = useMutation(graphql`
		mutation membrosNovoManualBatchImportMutation(
			$club: String!
			$input: ManualBatchImportMembersInput!
		) {
			manualBatchImportMembers(club: $club, manualBatchImportMembersInput: $input) {
				id
			}
		}
	`);

	const form = useForm({
		defaultValues: {
			importType: "batch" as "batch" | "manual",
			file: null as File | null,
			emails: "",
			accessType: "offers" as "offers" | "manual",
			offers: [] as string[],
			feeds: [] as string[],
			courses: [] as { id: string; memberGroup: string }[],
			tags: [] as string[],
			badges: [] as string[],
			sendInvites: true,
		},
		onSubmit: async ({ value }) => {
			setIsSubmitting(true);

			try {
				const commonPayload = {
					accessGroups:
						value.accessType === "offers" ? value.offers : undefined,
					spaces: value.feeds.length > 0 ? value.feeds : undefined,
					courses:
						value.courses.length > 0
							? value.courses.map((c) => ({
									id: c.id,
									memberGroup: c.memberGroup,
								}))
							: undefined,
					tags: value.tags.length > 0 ? value.tags : undefined,
					badges: value.badges.length > 0 ? value.badges : undefined,
					skipInvite: !value.sendInvites,
				};

				const onSuccess = (
					errors: ReadonlyArray<{ message: string }> | null,
				) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						setIsSubmitting(false);
						return;
					}
					toast.success("Membros importados com sucesso!");
					setIsSubmitting(false);

					if (canGoBack) {
						router.history.back();
					} else {
						navigate({ to: "/c/$club/audiencia/membros" });
					}
				};

				const onError = () => {
					toast.error(
						"Não foi possível importar os membros. Verifique os dados e tente novamente.",
					);
					setIsSubmitting(false);
				};

				if (value.importType === "batch") {
					if (!value.file) {
						toast.error("Selecione um arquivo para importar");
						setIsSubmitting(false);
						return;
					}

					const uploadedFile = await upload(value.file);

					batchImportMembers({
						variables: {
							club: club.slug,
							input: {
								file: uploadedFile.id,
								...commonPayload,
							},
						},
						onCompleted: (_response, errors) => onSuccess(errors),
						onError,
					});
				} else {
					const emails = value.emails
						.split(",")
						.map((e) => e.trim())
						.filter(Boolean);

					if (emails.length === 0) {
						toast.error("Insira pelo menos um e-mail");
						setIsSubmitting(false);
						return;
					}

					manualBatchImportMembers({
						variables: {
							club: club.slug,
							input: {
								emails,
								...commonPayload,
							},
						},
						onCompleted: (_response, errors) => onSuccess(errors),
						onError,
					});
				}
			} catch (error) {
				console.error("Error importing members:", error);
				toast.error(
					"Não foi possível importar os membros. Verifique os dados e tente novamente.",
				);
				setIsSubmitting(false);
			}
		},
	});

	const [importType, accessType, offers] = useStore(
		form.store,
		(state) =>
			[
				state.values.importType,
				state.values.accessType,
				state.values.offers,
			] as const,
	);

	const {
		getRootProps,
		getInputProps,
		isDragActive,
		open: openDropzone,
	} = useDropzone({
		noClick: true,
		accept: {
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
		},
		onDrop: ([file]) => {
			if (file) {
				form.setFieldValue("file", file);
			}
		},
	});

	const availableOffers = useMemo(() => {
		return data?.club?.offers?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.offers]);

	const availableFeeds = useMemo(() => {
		return (
			data?.club?.groups?.filter(Boolean).flatMap((group) =>
				group.spaces.filter(Boolean).map((space) => ({
					...space,
					groupName: group.name,
				})),
			) || []
		);
	}, [data?.club?.groups]);

	const availableCourses = useMemo(() => {
		return data?.club?.courses?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.courses]);

	const availableTags = useMemo(() => {
		return data?.club?.tags?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.tags]);

	const availableBadges = useMemo(() => {
		return data?.club?.badges?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.badges]);

	useEffect(() => {
		if (accessType === "offers") {
			const selectedOffers =
				availableOffers?.filter((off) => offers.includes(off.id)) || [];

			const selectedFeeds = Array.from(
				new Set(
					selectedOffers.flatMap((off) =>
						off.accessGroup.accessGroup.spaces.filter(Boolean).map((s) => s.id),
					),
				),
			);

			const selectedCoursesMemberGroup: Record<string, string> = {};

			selectedOffers.forEach((off) => {
				off.accessGroup.accessGroup.courses.forEach((course) => {
					if (course.memberGroup) {
						selectedCoursesMemberGroup[course.course.id] =
							course.memberGroup?.id;
					}
				});
			});

			const selectedCourses = Object.entries(selectedCoursesMemberGroup).map(
				([courseId, memberGroup]) => ({ id: courseId, memberGroup }),
			);

			form.setFieldValue("feeds", selectedFeeds);
			form.setFieldValue("courses", selectedCourses);
		}
	}, [accessType, offers, availableOffers, form]);

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[900px] flex flex-col gap-6 border rounded-md">
				<Heading>Adicionar Membros</Heading>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="flex flex-col gap-6"
				>
					<form.Field name="importType">
						{(field) => (
							<div>
								<Field>
									<FieldLabel className="select-none font-medium text-base/6 text-foreground data-disabled:opacity-50 sm:text-sm/6 dark:text-white">
										Tipo de Importação
									</FieldLabel>

									<FieldDescription>
										Importação em massa para subir um xlsx com vários emails de
										uma vez ou Importação manual para adicionar e-mails
										individualmente
									</FieldDescription>
								</Field>
								<div className="grid grid-cols-2 gap-3 mt-3 mb-0">
									<button
										type="button"
										data-checked={field.state.value === "batch"}
										className="flex h-[86px] cursor-pointer flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary! border"
										onClick={() => {
											field.setValue("batch");
											form.setFieldValue("emails", "");
											form.setFieldValue("file", null);
										}}
									>
										<div className="flex flex-col items-center justify-center pointer-events-none font-medium text-sm gap-1">
											<Icon name="FileSpreadsheet" className="size-5" />
											Em Massa
										</div>

										<div className="text-center text-xs text-muted-foreground">
											Importar via arquivo .xlsx
										</div>
									</button>

									<button
										type="button"
										data-checked={field.state.value === "manual"}
										className="flex h-[86px] cursor-pointer flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary! border"
										onClick={() => {
											field.setValue("manual");
											form.setFieldValue("file", null);
											form.setFieldValue("emails", "");
										}}
									>
										<div className="flex flex-col items-center justify-center pointer-events-none font-medium text-sm gap-1">
											<Icon name="PenLine" className="size-4" />
											Manual
										</div>

										<div className="text-center text-xs text-muted-foreground">
											Digitar e-mails manualmente
										</div>
									</button>
								</div>
							</div>
						)}
					</form.Field>

					{importType === "batch" && (
						<form.Field name="file">
							{(field) => (
								<Field>
									<FieldLabel>Arquivo</FieldLabel>

									<div className="flex flex-col items-center justify-center w-full gap-2">
										<input
											type="file"
											id={field.name}
											name={field.name}
											onChange={(e) => {
												if (e.target?.files?.[0]) {
													field.setValue(e.target.files[0]);
												}
											}}
											{...getInputProps()}
											className="hidden"
											accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
										/>

										<div {...getRootProps()} className="w-full">
											<button
												type="button"
												data-hover={isDragActive}
												className={cn(
													"size-full group overflow-clip relative flex items-center text-muted-foreground text-sm justify-center hover:border-accent-foreground flex-col h-40 border border-dashed rounded",
													!field.state.value &&
														"hover:bg-accent/10 hover:border-accent-foreground hover:text-accent-foreground",
													field.state.value && "relative bg-accent/5",
												)}
												onClick={() => openDropzone()}
											>
												{field.state.value ? (
													<>
														<Icon
															name="FileSpreadsheet"
															className="size-10 text-primary"
														/>
														<span className="mt-2 font-medium text-foreground">
															{field.state.value.name}
														</span>
														<span className="text-xs text-muted-foreground">
															{(field.state.value.size / 1024).toFixed(1)} KB
														</span>

														<div className="absolute group-data-[hover=true]:flex group-hover:flex hidden inset-0 bg-accent/45 text-accent-foreground z-2 flex-col items-center justify-center">
															<Icon name="CloudUpload" className="size-8" />
															Selecione outro arquivo ou arraste e solte aqui
														</div>
													</>
												) : (
													<>
														<Icon name="CloudUpload" className="size-8" />
														Selecione um arquivo .xlsx ou arraste e solte aqui
													</>
												)}
											</button>
										</div>

										{field.state.value && (
											<Badge
												variant="destructive"
												className="flex flex-row gap-2 cursor-pointer"
												onClick={() => field.handleChange(null)}
											>
												<Icon name="Trash2" className="size-4" />
												Remover
											</Badge>
										)}
									</div>

									<FieldError>{field.state.meta.errors.join(", ")}</FieldError>
								</Field>
							)}
						</form.Field>
					)}

					{importType === "manual" && (
						<>
							<form.Field name="emails">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>E-mails</FieldLabel>

										<Textarea
											id={field.name}
											name={field.name}
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="exemplo@email.com, outro@email.com"
											rows={6}
										/>

										<FieldDescription>
											Insira os e-mails separados por vírgula
										</FieldDescription>

										<FieldError>
											{field.state.meta.errors.join(", ")}
										</FieldError>
									</Field>
								)}
							</form.Field>

							<Alert>
								<Icon name="Info" className="size-4" />
								<AlertTitle>Acesso vitalício</AlertTitle>
								<AlertDescription>
									Membros adicionados manualmente terão acesso vitalício, sem
									data de expiração.
								</AlertDescription>
							</Alert>
						</>
					)}

					<form.Field name="accessType">
						{(field) => (
							<div>
								<Field>
									<FieldLabel>Gerenciar Acessos</FieldLabel>

									<FieldDescription>
										Defina quais espaços e cursos os membros importados terão
										acesso. Recomendamos que os acessos sejam concedidos através
										das ofertas, mas você pode concedê-los manualmente também
									</FieldDescription>
								</Field>

								<div className="grid grid-cols-2 gap-3 mt-3 mb-0">
									<button
										type="button"
										data-checked={field.state.value === "offers"}
										className="flex h-[86px] cursor-pointer flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary! border"
										onClick={() => field.setValue("offers")}
									>
										<div className="flex flex-col items-center justify-center pointer-events-none font-medium text-sm gap-1">
											<Icon name="Package" className="size-5" />
											Acessos atrelado a ofertas
										</div>
									</button>

									<button
										type="button"
										data-checked={field.state.value === "manual"}
										className="flex h-[86px] cursor-pointer flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary! border"
										onClick={() => field.setValue("manual")}
									>
										<div className="flex flex-col items-center justify-center pointer-events-none font-medium text-sm gap-1">
											<Icon name="HandHelping" className="size-5" />
											Acessos Manuais
										</div>
									</button>
								</div>
							</div>
						)}
					</form.Field>

					{accessType === "offers" && (
						<form.Field name="offers" mode="array">
							{(field) => (
								<Field>
									<FieldLabel>Selecionar Ofertas</FieldLabel>

									<Popover.Root>
										<Popover.Trigger asChild>
											<Button
												variant="outline"
												className="max-w-full overflow-x-auto w-full overflow-y-hidden"
											>
												{field.state.value.length > 0 ? (
													<span className="flex-1 line-clamp-1 text-left font-normal">
														{field.state.value
															.map((val) => {
																const offer = availableOffers.find(
																	(o) => o.id === val,
																);
																return offer?.productName;
															})
															.filter(Boolean)
															.join(", ")}
													</span>
												) : (
													<span className="flex-1 dark:text-white/60 line-clamp-1 text-left font-normal text-muted-foreground">
														Selecionar ofertas
													</span>
												)}

												<Icon name="ChevronsUpDown" className="size-4" />
											</Button>
										</Popover.Trigger>

										<Popover.Portal>
											<Popover.Content
												sideOffset={5}
												className="flex z-50 w-(--radix-popover-trigger-width) border overflow-y-auto rounded-md flex-col items-start bg-card shadow origin-[--radix-popover-content-transform-origin] max-h-[calc(var(--radix-popover-content-available-height)-24px)]"
											>
												{availableOffers.length === 0 ? (
													<div className="py-4 px-3 text-sm text-muted-foreground">
														Nenhuma oferta disponível
													</div>
												) : (
													availableOffers.map((offer) => (
														<button
															type="button"
															key={offer.id}
															className="py-2.5 hover:bg-accent hover:text-accent-foreground pr-10 flex items-center justify-start gap-2 px-2 rounded w-full"
															onClick={() => {
																const isAdding = !field.state.value.includes(
																	offer.id,
																);
																if (isAdding) {
																	field.pushValue(offer.id);
																} else {
																	field.removeValue(
																		field.state.value.indexOf(offer.id),
																	);
																}
															}}
														>
															<Checkbox
																checked={field.state.value.includes(offer.id)}
																className="pointer-events-none"
																color="blue"
															/>
															{offer.productName}
														</button>
													))
												)}
											</Popover.Content>
										</Popover.Portal>
									</Popover.Root>
								</Field>
							)}
						</form.Field>
					)}

					<div className="grid grid-cols-2 gap-4">
						<form.Field name="feeds" mode="array">
							{(field) => (
								<FeedsSelector
									feeds={availableFeeds}
									selectedFeeds={field.state.value}
									onFeedToggle={(feedId) => {
										if (!field.state.value.includes(feedId)) {
											field.pushValue(feedId);
										} else {
											field.removeValue(field.state.value.indexOf(feedId));
										}
									}}
									disabled={accessType === "offers"}
									hideDisabledUnchecked
								/>
							)}
						</form.Field>

						<form.Field name="courses" mode="array">
							{(field) => (
								<CoursesSelector
									courses={availableCourses}
									selectedCourses={field.state.value}
									onCourseToggle={(courseId, memberGroupId) => {
										const index = field.state.value.findIndex(
											(c) => c.id === courseId,
										);
										if (index === -1) {
											field.pushValue({
												id: courseId,
												memberGroup: memberGroupId,
											});
										} else {
											field.removeValue(index);
										}
									}}
									onMemberGroupChange={(courseId, memberGroupId) => {
										const index = field.state.value.findIndex(
											(c) => c.id === courseId,
										);
										if (index !== -1) {
											const newValue = [...field.state.value];
											newValue[index] = {
												id: courseId,
												memberGroup: memberGroupId,
											};
											field.setValue(newValue);
										}
									}}
									onCreateMemberGroup={setCreateMemberGroupForCourse}
									disabled={accessType === "offers"}
									hideDisabledUnchecked
								/>
							)}
						</form.Field>
					</div>

					<Collapsible.Root>
						<Collapsible.Trigger className="flex group mb-3 flex-row items-center justify-start gap-2 font-medium select-none text-base/6 text-foreground data-disabled:opacity-50 sm:text-sm/6 dark:text-white">
							Configurações Avançadas
							<Icon
								name="ChevronDown"
								className="group-data-[state=open]:rotate-180 size-4 text-muted-foreground"
							/>
						</Collapsible.Trigger>

						<Collapsible.Content className="flex flex-col gap-4">
							<form.Field name="tags" mode="array">
								{(field) => (
									<Field>
										<FieldLabel>Associar Tags</FieldLabel>

										<Popover.Root>
											<Popover.Trigger asChild>
												<Button
													variant="outline"
													className="max-w-full overflow-x-auto w-full overflow-y-hidden"
												>
													{field.state.value.length > 0 ? (
														<span className="flex-1 line-clamp-1 text-left font-normal">
															{field.state.value
																.map((val) => {
																	const tag = availableTags.find(
																		(t) => t.id === val,
																	);
																	return tag?.text;
																})
																.filter(Boolean)
																.join(", ")}
														</span>
													) : (
														<span className="flex-1 dark:text-white/60 line-clamp-1 text-left font-normal text-muted-foreground">
															Selecionar tags
														</span>
													)}

													<Icon name="ChevronsUpDown" className="size-4" />
												</Button>
											</Popover.Trigger>

											<Popover.Portal>
												<Popover.Content
													sideOffset={5}
													className="flex z-50 w-(--radix-popover-trigger-width) border overflow-y-auto rounded-md flex-col items-start bg-card shadow origin-[--radix-popover-content-transform-origin] max-h-[calc(var(--radix-popover-content-available-height)-24px)]"
												>
													{availableTags.length === 0 ? (
														<div className="py-4 px-3 text-sm text-muted-foreground">
															Nenhuma tag disponível
														</div>
													) : (
														availableTags.map((tag) => (
															<button
																type="button"
																key={tag.id}
																className="py-2.5 hover:bg-accent hover:text-accent-foreground pr-10 flex items-center justify-start gap-2 px-2 rounded w-full"
																onClick={() => {
																	if (!field.state.value.includes(tag.id)) {
																		field.pushValue(tag.id);
																	} else {
																		field.removeValue(
																			field.state.value.indexOf(tag.id),
																		);
																	}
																}}
															>
																<Checkbox
																	checked={field.state.value.includes(tag.id)}
																	className="pointer-events-none"
																	color="blue"
																/>
																{tag.text}
															</button>
														))
													)}
												</Popover.Content>
											</Popover.Portal>
										</Popover.Root>
									</Field>
								)}
							</form.Field>

							<form.Field name="badges" mode="array">
								{(field) => (
									<Field>
										<FieldLabel>Associar Emblemas</FieldLabel>

										<Popover.Root>
											<Popover.Trigger asChild>
												<Button
													variant="outline"
													className="max-w-full overflow-x-auto w-full overflow-y-hidden"
												>
													{field.state.value.length > 0 ? (
														<span className="flex-1 flex flex-wrap gap-1 text-left font-normal">
															{field.state.value.map((val) => {
																const badge = availableBadges.find(
																	(b) => b.id === val,
																);
																if (!badge) return null;
																return (
																	<MemberBadge key={badge.id} badge={badge} />
																);
															})}
														</span>
													) : (
														<span className="flex-1 dark:text-white/60 line-clamp-1 text-left font-normal text-muted-foreground">
															Selecionar emblemas
														</span>
													)}

													<Icon name="ChevronsUpDown" className="size-4" />
												</Button>
											</Popover.Trigger>

											<Popover.Portal>
												<Popover.Content
													sideOffset={5}
													className="flex z-50 w-(--radix-popover-trigger-width) border overflow-y-auto rounded-md flex-col items-start bg-card shadow origin-[--radix-popover-content-transform-origin] max-h-[calc(var(--radix-popover-content-available-height)-24px)]"
												>
													{availableBadges.length === 0 ? (
														<div className="py-4 px-3 text-sm text-muted-foreground">
															Nenhum emblema disponível
														</div>
													) : (
														availableBadges.map((badge) => (
															<button
																type="button"
																key={badge.id}
																className="py-2.5 hover:bg-accent hover:text-accent-foreground pr-10 flex items-center justify-start gap-2 px-2 rounded w-full"
																onClick={() => {
																	if (!field.state.value.includes(badge.id)) {
																		field.pushValue(badge.id);
																	} else {
																		field.removeValue(
																			field.state.value.indexOf(badge.id),
																		);
																	}
																}}
															>
																<Checkbox
																	checked={field.state.value.includes(badge.id)}
																	className="pointer-events-none"
																	color="blue"
																/>
																<MemberBadge badge={badge} />
															</button>
														))
													)}
												</Popover.Content>
											</Popover.Portal>
										</Popover.Root>
									</Field>
								)}
							</form.Field>

							<form.Field name="sendInvites">
								{(field) => (
									<Field orientation="horizontal">
										<div className="flex flex-col items-start justify-center gap-1.5">
											<FieldLabel>Enviar Convites agora</FieldLabel>

											<FieldDescription>
												Enviar e-mail de convite para os membros adicionados
											</FieldDescription>
										</div>

										<Switch
											color="primary"
											checked={field.state.value}
											onCheckedChange={(val) => field.setValue(val)}
										/>
									</Field>
								)}
							</form.Field>
						</Collapsible.Content>
					</Collapsible.Root>

					<div className="flex justify-end gap-3">
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? (
								<>
									<Icon name="LoaderCircle" className="size-4 animate-spin" />
									Importando...
								</>
							) : (
								"Adicionar Membros"
							)}
						</Button>
					</div>
				</form>
			</div>

			<CreateMemberGroupDialog
				course={createMemberGroupForCourse}
				open={!!createMemberGroupForCourse}
				onClose={() => setCreateMemberGroupForCourse(null)}
			/>
		</div>
	);
}
